import React, { useEffect, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import ApiService from '../../../_services/ApiService';

interface AddResourceModalProps {
  show: boolean;
  activitySlotId: number | null;
  onClose: () => void;
  onSuccess?: () => void;
}

interface ResultSummary {
  inserted: number;
  skipped: number;
  invalid: number[];
}

const parseIds = (raw: string): number[] => {
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .map((part) => Number(part))
    .filter((n) => Number.isInteger(n) && n > 0);
};

const AddResourceModal: React.FC<AddResourceModalProps> = ({
  show,
  activitySlotId,
  onClose,
  onSuccess,
}) => {
  const [rawIds, setRawIds] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ResultSummary | null>(null);

  useEffect(() => {
    if (show) {
      setRawIds('');
      setError(null);
      setResult(null);
      setSubmitting(false);
    }
  }, [show]);

  const parsedIds = parseIds(rawIds);
  const canSubmit = !submitting && activitySlotId !== null && parsedIds.length > 0;

  const handleSubmit = async () => {
    if (!canSubmit || activitySlotId === null) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const response = await ApiService.attachResourcesToActivitySlot(
        activitySlotId,
        parsedIds,
      );
      const data = response?.data?.data ?? response?.data ?? {};
      const summary: ResultSummary = {
        inserted: Number(data.inserted ?? 0),
        skipped: Number(data.skipped ?? 0),
        invalid: Array.isArray(data.invalid) ? data.invalid : [],
      };
      setResult(summary);
      if (summary.inserted > 0 && onSuccess) onSuccess();
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to attach resources.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Resource</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ marginBottom: 12 }}>
          Enter the Study Resource IDs you want to attach to this conducted
          class. Use commas to separate multiple IDs.
        </p>
        <Form.Control
          type="text"
          placeholder="e.g. 1024, 1198, 2003"
          value={rawIds}
          onChange={(e) => {
            setRawIds(e.target.value);
            setError(null);
            setResult(null);
          }}
          disabled={submitting}
          autoFocus
        />
        {parsedIds.length > 0 && (
          <small style={{ color: 'var(--text-muted)', display: 'block', marginTop: 6 }}>
            {parsedIds.length} valid id(s) parsed.
          </small>
        )}
        {error && (
          <div style={{ color: 'var(--status-danger)', marginTop: 12 }}>{error}</div>
        )}
        {result && (
          <div style={{ marginTop: 12 }}>
            <div>Attached: <strong>{result.inserted}</strong></div>
            {result.skipped > 0 && (
              <div>Already attached (skipped): <strong>{result.skipped}</strong></div>
            )}
            {result.invalid.length > 0 && (
              <div style={{ color: 'var(--status-danger)' }}>
                Unknown resource ids: {result.invalid.join(', ')}
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={submitting}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && (
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? 'Attaching...' : 'Attach'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default AddResourceModal;
